import { AuthUserPermission, getMpAgent } from '@cibo/core'
import { Can, usePermissions } from '@cibo/profile'
import { debugSx } from '@cibo/ui'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useProgramEngagementContext } from '../../../../context'

type ContextParamSource = 'engagementId' | 'taskId'

interface ContextParamMapping {
  /** The key to use in the request body */
  param: string
  /** Which context value to read. Use 'engagementId' to send the engagement id as 'id'. */
  from: ContextParamSource
}

interface StaticParamConfig {
  /** The key to use in the request body */
  param: string
  /** Fixed value from config (not user-editable) */
  value: string | boolean | number
}

interface ParamFieldConfig {
  /** The key to use in the request body */
  name: string
  type: 'boolean'
  label: string
  defaultValue?: boolean
  /** When true, the field is only rendered for debug_user and only included in the payload for debug_user */
  debugOnly?: boolean
}

export interface ActionRequestButtonProps {
  /** Button label */
  label: string
  /** API endpoint path (relative), e.g. '/eqip/inferAndUpdateContractData' */
  url: string
  /**
   * Maps context values to request body params.
   * Example: [{ param: 'id', from: 'engagementId' }] sends the current engagementId as 'id'.
   */
  contextParams?: ContextParamMapping[]
  /** Fixed config values added to every request body */
  staticParams?: StaticParamConfig[]
  /** User-editable params rendered as form controls alongside the button */
  params?: ParamFieldConfig[]
  /** When set, the button is disabled unless this workflow tag is present on the engagement */
  enabledTag?: string
  'data-testid'?: string
}

export const ActionRequestButton = ({
  label,
  url,
  contextParams = [],
  staticParams = [],
  params = [],
  enabledTag,
  'data-testid': dataTestId = 'action-request-button',
}: ActionRequestButtonProps) => {
  const { t } = useTranslation('@cibo/programs/ActionRequestButton')
  const { engagementId, taskId, refreshEngagement, engagement } = useProgramEngagementContext()
  const { isDebugUser } = usePermissions()
  const { enqueueSnackbar } = useSnackbar()

  const [paramValues, setParamValues] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      params.filter(p => p.type === 'boolean').map(p => [p.name, p.defaultValue ?? false])
    )
  )

  const [isPending, setIsPending] = useState(false)

  const contextValueMap: Record<ContextParamSource, string | undefined> = { engagementId, taskId }

  const requiredContextMissing = contextParams.some(cp => !contextValueMap[cp.from])
  const missingEnabledTag =
    !!enabledTag && !engagement.data?.wfTags?.includes(enabledTag)

  const handleClick = async () => {
    const body: Record<string, unknown> = {}

    for (const cp of contextParams) {
      const value = contextValueMap[cp.from]
      if (value) body[cp.param] = value
    }

    for (const sp of staticParams) {
      body[sp.param] = sp.value
    }

    for (const p of params) {
      if (!p.debugOnly || isDebugUser) {
        body[p.name] = paramValues[p.name]
      }
    }

    setIsPending(true)
    try {
      await getMpAgent().post(url, body, { timeout: 120_000 })
      refreshEngagement()
    } catch {
      enqueueSnackbar(t('error'), { variant: 'error' })
    } finally {
      setIsPending(false)
    }
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Button
          variant="contained"
          disabled={isPending || requiredContextMissing || missingEnabledTag}
          onClick={handleClick}
          data-testid={dataTestId}
          endIcon={isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {label}
        </Button>

        {params.map(p => {
          const control = (
            <FormControlLabel
              key={p.name}
              sx={p.debugOnly ? { ...debugSx, pr: 1 } : { pr: 1 }}
              control={
                <Checkbox
                  checked={paramValues[p.name]}
                  onChange={e => setParamValues(prev => ({ ...prev, [p.name]: e.target.checked }))}
                  data-testid={`action-request-param-${p.name}`}
                />
              }
              label={
                <Typography variant="caption" color={p.debugOnly ? 'error' : 'textSecondary'}>
                  {p.label}
                </Typography>
              }
            />
          )

          return p.debugOnly ? (
            <Can key={p.name} useAny={[AuthUserPermission.DEBUG_USER]}>
              {control}
            </Can>
          ) : (
            control
          )
        })}
      </Stack>
    </Box>
  )
}
